import { html, render, nothing, type TemplateResult } from 'lit-html';
import heatmapStyles from './pk-report-heatmap.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const TOOLTIP_OFFSET_X = 14;
const TOOLTIP_OFFSET_Y = 10;

/** 5-step intensity palette (step 0 = zero/empty, handled separately). */
const INTENSITY_STEPS = 5;

/** Hue for intensity mode: use CSS var with teal fallback (matches pk-color-primary). */
const INTENSITY_HUE = 'var(--pk-color-primary-h, 183)'; // 183° ≈ teal

/** Blue hue for diverging negative. Red hue for diverging positive. */
const DIVERGING_NEG_HUE = '216'; // blue
const DIVERGING_POS_HUE = '4';   // red

// ─────────────────────────────────────────────────────────────────────────────
// Value formatting helpers
// ─────────────────────────────────────────────────────────────────────────────

type ValueFormat = 'number' | 'currency' | 'bytes' | 'ms' | 'percent';

function formatValue(v: number, fmt: ValueFormat): string {
    switch (fmt) {
        case 'currency':
            return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        case 'bytes': {
            if (Math.abs(v) < 1024) return v.toFixed(0) + ' B';
            if (Math.abs(v) < 1024 * 1024) return (v / 1024).toFixed(1) + ' KB';
            if (Math.abs(v) < 1024 * 1024 * 1024) return (v / (1024 * 1024)).toFixed(1) + ' MB';
            return (v / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        }
        case 'ms':
            if (Math.abs(v) < 1000) return v.toFixed(0) + ' ms';
            if (Math.abs(v) < 60_000) return (v / 1000).toFixed(1) + ' s';
            return (v / 60_000).toFixed(1) + ' min';
        case 'percent':
            return v.toFixed(1) + '%';
        default:
            return v.toLocaleString();
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// Color computation
// ─────────────────────────────────────────────────────────────────────────────

type ColorScheme = 'intensity' | 'diverging';

/**
 * Returns a CSS color string for a cell given its value and the overall range.
 * Uses 5 discrete intensity steps for `intensity` mode (cleaner SIEM-style
 * "is this risky?" read).
 */
function computeCellColor(value: number, min: number, max: number, scheme: ColorScheme): string {
    if (value === 0 && min >= 0) {
        // Explicit zero in an all-positive range = empty sentinel
        return 'var(--pk-color-bg-sunken, #FBFAF5)';
    }

    if (scheme === 'intensity') {
        // Step 0 (zero) already handled above.
        // Map to 5 discrete steps: each step = (max - min) / 5.
        const range = max - min;
        if (range === 0) return `hsl(${INTENSITY_HUE}, 55%, 50%)`;
        const ratio = Math.max(0, Math.min(1, (value - min) / range));
        // step 1-5
        const step = Math.ceil(ratio * INTENSITY_STEPS) || 1;
        const lightness = 90 - step * 13;  // step1=77%, step2=64%, step3=51%, step4=38%, step5=25%
        const saturation = 30 + step * 12; // step1=42%, step2=54%, step3=66%, step4=78%, step5=90%
        return `hsl(${INTENSITY_HUE}, ${saturation}%, ${lightness}%)`;
    }

    // diverging: negatives = blue, positives = red, zero = near-white
    if (value === 0) return 'hsl(0, 0%, 93%)';
    const absMax = Math.max(Math.abs(min), Math.abs(max), 1);
    const ratio = Math.min(1, Math.abs(value) / absMax);
    const step = Math.ceil(ratio * INTENSITY_STEPS) || 1;
    const lightness   = 90 - step * 12;
    const saturation  = 30 + step * 11;
    const hue = value < 0 ? DIVERGING_NEG_HUE : DIVERGING_POS_HUE;
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal data types
// ─────────────────────────────────────────────────────────────────────────────

interface CellData {
    column:    string;
    value:     number;
    dataExtra: string | null;
    /** resolved color — set during render */
    color?:    string;
}

interface RowData {
    label: string;
    cells: CellData[];
}

// ─────────────────────────────────────────────────────────────────────────────
// <pk-report-heatmap>
// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-heatmap>` — 2D cell grid where row × column position carries
 * one categorical dimension each; cell color encodes a numeric value. v0.7.1
 *
 * THE carry-over TODO #5 from Phase 1: promotes the SIEM CSS-grid change-volume
 * heatmap to a proper PortalKit component (Reports-Packs-Spec.md § 8 TODO #5).
 *
 * Accepts `<pk-report-heatmap-row label="…">` children, each containing one
 * or more `<pk-report-heatmap-cell column="…" value="…">` children.
 *
 * Attributes:
 *   `height`           — total component height in px (default 320)
 *   `color-scheme`     — `intensity` (default) | `diverging`
 *   `value-format`     — `number` | `currency` | `bytes` | `ms` | `percent` (default `number`)
 *   `row-label-width`  — left-gutter width for row labels in px (default 140)
 *   `empty-message`    — text shown when no rows are present
 *   `interactive`      — boolean, default true
 *
 * Events:
 *   `pk-cell-click` — `detail: { rowLabel, column, value, dataExtra }`
 *                     fires on cell click or Enter key when interactive.
 *
 * Keyboard (when interactive):
 *   Arrow keys move focus through cells (left/right within row, up/down across rows).
 *   Enter fires `pk-cell-click`. Tab cycles through row labels.
 *
 * ARIA: `role="table"` on the root grid; `role="row"` on row groups;
 *   `role="rowheader"` on row labels; `role="cell"` on each data cell with
 *   `aria-label="{row} on {column}: {formatted value}"`.
 *
 * ```html
 * <pk-report-heatmap height="320" color-scheme="intensity">
 *   <pk-report-heatmap-row label="sub-prod-01">
 *     <pk-report-heatmap-cell column="2026-05-17" value="0"></pk-report-heatmap-cell>
 *     <pk-report-heatmap-cell column="2026-05-18" value="3"></pk-report-heatmap-cell>
 *   </pk-report-heatmap-row>
 * </pk-report-heatmap>
 * ```
 */
export class PkReportHeatmap extends HTMLElement {
    static readonly tagName = 'pk-report-heatmap';

    static get observedAttributes(): string[] {
        return ['height', 'color-scheme', 'value-format', 'row-label-width',
                'empty-message', 'interactive'];
    }

    private readonly _root: ShadowRoot;
    private readonly _mutationObserver: MutationObserver;

    // Tooltip state
    private _tooltipRow    = '';
    private _tooltipCol    = '';
    private _tooltipValue  = 0;
    private _tooltipX      = 0;
    private _tooltipY      = 0;
    private _tooltipVisible = false;

    // Focused cell [rowIdx, colIdx]
    private _focusedRow = -1;
    private _focusedCol = -1;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._mutationObserver = new MutationObserver(() => this._doRender());
    }

    connectedCallback(): void {
        this._mutationObserver.observe(this, { childList: true, subtree: true, attributes: true });
        this._doRender();
    }

    disconnectedCallback(): void {
        this._mutationObserver.disconnect();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    // ── Attribute helpers ─────────────────────────────────────────────────────

    private _height(): number {
        const v = parseInt(this.getAttribute('height') ?? '320', 10);
        return isNaN(v) || v <= 0 ? 320 : v;
    }

    private _colorScheme(): ColorScheme {
        return this.getAttribute('color-scheme') === 'diverging' ? 'diverging' : 'intensity';
    }

    private _valueFormat(): ValueFormat {
        const v = this.getAttribute('value-format') ?? 'number';
        const valid: ValueFormat[] = ['number', 'currency', 'bytes', 'ms', 'percent'];
        return (valid.includes(v as ValueFormat) ? v : 'number') as ValueFormat;
    }

    private _rowLabelWidth(): number {
        const v = parseInt(this.getAttribute('row-label-width') ?? '140', 10);
        return isNaN(v) || v <= 0 ? 140 : v;
    }

    private _emptyMessage(): string {
        return this.getAttribute('empty-message') ?? 'No data in the selected time range.';
    }

    private _isInteractive(): boolean {
        const v = this.getAttribute('interactive');
        return v === null || v === '' || v === 'true';
    }

    // ── Data collection ──────────────────────────────────────────────────────

    private _collectRows(): RowData[] {
        const rowEls = Array.from(this.querySelectorAll('pk-report-heatmap-row'));
        return rowEls.map(rowEl => {
            const label = rowEl.getAttribute('label') ?? '';
            const cellEls = Array.from(rowEl.querySelectorAll('pk-report-heatmap-cell'));
            const cells: CellData[] = cellEls.map(cellEl => ({
                column:    cellEl.getAttribute('column') ?? '',
                value:     parseFloat(cellEl.getAttribute('value') ?? '0') || 0,
                dataExtra: cellEl.getAttribute('data-extra'),
            }));
            return { label, cells };
        });
    }

    /** Collect all unique column keys (in order of first appearance). */
    private _allColumns(rows: RowData[]): string[] {
        const seen = new Set<string>();
        const cols: string[] = [];
        for (const row of rows) {
            for (const cell of row.cells) {
                if (!seen.has(cell.column)) {
                    seen.add(cell.column);
                    cols.push(cell.column);
                }
            }
        }
        return cols;
    }

    /** Build a lookup: rowIdx → colKey → CellData */
    private _buildMatrix(rows: RowData[], cols: string[]): (CellData | null)[][] {
        return rows.map(row => {
            const byCol = new Map(row.cells.map(c => [c.column, c]));
            return cols.map(col => byCol.get(col) ?? null);
        });
    }

    /** Compute min/max across all cells (excluding null). */
    private _valueRange(rows: RowData[]): { min: number; max: number } {
        let min = Infinity;
        let max = -Infinity;
        for (const row of rows) {
            for (const cell of row.cells) {
                if (cell.value < min) min = cell.value;
                if (cell.value > max) max = cell.value;
            }
        }
        if (min === Infinity) return { min: 0, max: 0 };
        return { min, max };
    }

    // ── Event dispatch ────────────────────────────────────────────────────────

    private _fireCellClick(rowLabel: string, column: string, value: number, dataExtra: string | null): void {
        if (!this._isInteractive()) return;
        this.dispatchEvent(new CustomEvent('pk-cell-click', {
            bubbles:  true,
            composed: true,
            detail: { rowLabel, column, value, dataExtra },
        }));
    }

    // ── Tooltip handlers ──────────────────────────────────────────────────────

    private _showTooltip(row: string, col: string, value: number, x: number, y: number): void {
        this._tooltipRow    = row;
        this._tooltipCol    = col;
        this._tooltipValue  = value;
        this._tooltipX      = x + TOOLTIP_OFFSET_X;
        this._tooltipY      = y + TOOLTIP_OFFSET_Y;
        this._tooltipVisible = true;
        this._doRender();
    }

    private _moveTooltip(x: number, y: number): void {
        if (!this._tooltipVisible) return;
        this._tooltipX = x + TOOLTIP_OFFSET_X;
        this._tooltipY = y + TOOLTIP_OFFSET_Y;
        this._doRender();
    }

    private _hideTooltip(): void {
        this._tooltipVisible = false;
        this._doRender();
    }

    // ── Keyboard ──────────────────────────────────────────────────────────────

    private _handleCellKeydown(
        e: KeyboardEvent,
        rowIdx: number,
        colIdx: number,
        rows: RowData[],
        cols: string[],
        matrix: (CellData | null)[][],
        min: number
    ): void {
        const numRows = rows.length;
        const numCols = cols.length;

        switch (e.key) {
            case 'ArrowRight': {
                e.preventDefault();
                const nextCol = Math.min(numCols - 1, colIdx + 1);
                this._focusedRow = rowIdx;
                this._focusedCol = nextCol;
                this._doRender();
                this._focusCellEl(rowIdx, nextCol);
                break;
            }
            case 'ArrowLeft': {
                e.preventDefault();
                const prevCol = Math.max(0, colIdx - 1);
                this._focusedRow = rowIdx;
                this._focusedCol = prevCol;
                this._doRender();
                this._focusCellEl(rowIdx, prevCol);
                break;
            }
            case 'ArrowDown': {
                e.preventDefault();
                const nextRow = Math.min(numRows - 1, rowIdx + 1);
                this._focusedRow = nextRow;
                this._focusedCol = colIdx;
                this._doRender();
                this._focusCellEl(nextRow, colIdx);
                break;
            }
            case 'ArrowUp': {
                e.preventDefault();
                const prevRow = Math.max(0, rowIdx - 1);
                this._focusedRow = prevRow;
                this._focusedCol = colIdx;
                this._doRender();
                this._focusCellEl(prevRow, colIdx);
                break;
            }
            case 'Enter': {
                e.preventDefault();
                const cell = matrix[rowIdx]?.[colIdx];
                const row  = rows[rowIdx];
                const col  = cols[colIdx];
                if (cell && row && col) {
                    const isEmpty = cell.value === 0 && min >= 0;
                    if (!isEmpty) {
                        this._fireCellClick(row.label, col, cell.value, cell.dataExtra);
                    }
                }
                break;
            }
            case 'Escape': {
                this._focusedRow = -1;
                this._focusedCol = -1;
                this._doRender();
                break;
            }
        }
    }

    private _focusCellEl(rowIdx: number, colIdx: number): void {
        requestAnimationFrame(() => {
            const el = this._root.querySelector<HTMLElement>(
                `[data-cell-row="${rowIdx}"][data-cell-col="${colIdx}"]`
            );
            el?.focus();
        });
    }

    // ── Column label formatting ───────────────────────────────────────────────

    private _formatColumnLabel(col: string): string {
        // Try to parse as ISO date and abbreviate to MM/DD
        try {
            const d = new Date(col);
            if (!isNaN(d.getTime())) {
                return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            }
        } catch { /* not a date */ }
        // Fallback: truncate long strings
        return col.length > 8 ? col.slice(0, 7) + '…' : col;
    }

    // ── Template ──────────────────────────────────────────────────────────────

    private _template(): TemplateResult {
        const height       = this._height();
        const scheme       = this._colorScheme();
        const fmt          = this._valueFormat();
        const labelWidth   = this._rowLabelWidth();
        const interactive  = this._isInteractive();

        const rows    = this._collectRows();
        const cols    = this._allColumns(rows);
        const matrix  = this._buildMatrix(rows, cols);
        const { min, max } = this._valueRange(rows);

        // Empty state
        if (rows.length === 0) {
            return html`
                <style>${sharedTokens}${heatmapStyles}</style>
                <div class="heatmap-panel" style="min-height: ${height}px">
                    <div class="heatmap-empty">
                        <p class="heatmap-empty-message">${this._emptyMessage()}</p>
                        <div class="heatmap-empty-slot">
                            <slot name="empty-state"></slot>
                        </div>
                    </div>
                </div>
                <!-- hidden children slot -->
                <slot style="display:none"></slot>
            `;
        }

        // CSS grid: [labelWidth]px + (numCols * 1fr)
        const colTemplate = `${labelWidth}px repeat(${cols.length}, minmax(var(--pk-report-heatmap-cell-min-size, 2rem), 1fr))`;

        // Build grid rows: header row + data rows
        const headerRow = html`
            <div class="heatmap-col-header-row" role="row">
                <div class="heatmap-col-corner" role="columnheader" aria-label="Row labels"></div>
                ${cols.map(col => html`
                    <div class="heatmap-col-header" role="columnheader" title="${col}">
                        ${this._formatColumnLabel(col)}
                    </div>
                `)}
            </div>
        `;

        const dataRows = rows.map((row, ri) => html`
            <div class="heatmap-row" role="row" data-row-idx="${ri}">
                <div class="heatmap-row-label"
                     role="rowheader"
                     tabindex="${interactive ? '0' : '-1'}"
                     title="${row.label}">
                    ${row.label}
                </div>
                ${cols.map((col, ci) => {
                    const cell = matrix[ri]?.[ci];
                    const value = cell?.value ?? 0;
                    const isEmpty = cell === null || (value === 0 && min >= 0);
                    const color = isEmpty
                        ? 'var(--pk-color-bg-sunken, #FBFAF5)'
                        : computeCellColor(value, min, max, scheme);
                    const ariaLabel = `${row.label} on ${col}: ${formatValue(value, fmt)}`;
                    const isFocused = this._focusedRow === ri && this._focusedCol === ci;

                    return html`
                        <div class="heatmap-cell ${isEmpty ? 'heatmap-cell--empty' : ''}"
                             role="cell"
                             aria-label="${ariaLabel}"
                             data-interactive="${interactive}"
                             data-cell-row="${ri}"
                             data-cell-col="${ci}"
                             tabindex="${interactive && !isEmpty ? (isFocused ? '0' : '-1') : '-1'}"
                             style="background: ${color};"
                             @mouseenter=${(e: MouseEvent) => {
                                 if (!isEmpty) {
                                     this._showTooltip(row.label, col, value, e.clientX, e.clientY);
                                 }
                             }}
                             @mousemove=${(e: MouseEvent) => this._moveTooltip(e.clientX, e.clientY)}
                             @mouseleave=${() => this._hideTooltip()}
                             @click=${() => {
                                 // Only fire for non-empty cells (cell exists and has a non-zero value,
                                 // or value is meaningful in diverging mode).
                                 if (interactive && cell && !isEmpty) {
                                     this._fireCellClick(row.label, col, value, cell.dataExtra ?? null);
                                 }
                             }}
                             @keydown=${(e: KeyboardEvent) =>
                                 interactive && this._handleCellKeydown(e, ri, ci, rows, cols, matrix, min)}>
                        </div>
                    `;
                })}
            </div>
        `);

        // Tooltip
        const tooltip = this._tooltipVisible
            ? html`
                <div class="heatmap-tooltip"
                     style="left: ${this._tooltipX}px; top: ${this._tooltipY}px"
                     role="tooltip"
                     aria-hidden="true">
                    <div class="tooltip-row-label">${this._tooltipRow}</div>
                    <div class="tooltip-column">${this._tooltipCol}</div>
                    <div class="tooltip-value">${formatValue(this._tooltipValue, fmt)}</div>
                </div>
              `
            : nothing;

        return html`
            <style>${sharedTokens}${heatmapStyles}</style>
            <div class="heatmap-panel"
                 style="max-height: ${height}px; overflow-y: auto;">
                <div class="heatmap-scroll">
                    <div class="heatmap-table"
                         role="table"
                         aria-label="Heatmap"
                         style="grid-template-columns: ${colTemplate};">
                        ${headerRow}
                        ${dataRows}
                    </div>
                </div>
            </div>
            ${tooltip}
            <!-- hidden children slot -->
            <slot style="display:none"></slot>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportHeatmap.tagName)) {
    customElements.define(PkReportHeatmap.tagName, PkReportHeatmap);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-heatmap-row>` — a single row within `<pk-report-heatmap>`. v0.7.1
 *
 * Data-only element; no visual shadow DOM. Parent reads its `label` attribute
 * and child `<pk-report-heatmap-cell>` elements.
 *
 * Attributes:
 *   `label` — row header label (required).
 */
export class PkReportHeatmapRow extends HTMLElement {
    static readonly tagName = 'pk-report-heatmap-row';

    static get observedAttributes(): string[] {
        return ['label'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-heatmap')?.dispatchEvent(
            new CustomEvent('_row-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportHeatmapRow.tagName)) {
    customElements.define(PkReportHeatmapRow.tagName, PkReportHeatmapRow);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-heatmap-cell>` — a single data cell within a heatmap row. v0.7.1
 *
 * Data-only element. The parent `<pk-report-heatmap>` reads `column`, `value`,
 * and `data-extra`; renders the cell with a computed color based on the
 * component-wide min/max range.
 *
 * Attributes:
 *   `column`     — column key (usually a date or category string, required).
 *   `value`      — numeric value driving the color + tooltip (required).
 *   `data-extra` — opaque string passed back in `pk-cell-click` detail for drill-through.
 */
export class PkReportHeatmapCell extends HTMLElement {
    static readonly tagName = 'pk-report-heatmap-cell';

    static get observedAttributes(): string[] {
        return ['column', 'value', 'data-extra'];
    }

    attributeChangedCallback(): void {
        this.closest('pk-report-heatmap')?.dispatchEvent(
            new CustomEvent('_cell-update', { bubbles: false })
        );
    }
}

if (!customElements.get(PkReportHeatmapCell.tagName)) {
    customElements.define(PkReportHeatmapCell.tagName, PkReportHeatmapCell);
}

// ─────────────────────────────────────────────────────────────────────────────
// Global type augmentation
// ─────────────────────────────────────────────────────────────────────────────

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-heatmap':      PkReportHeatmap;
        'pk-report-heatmap-row':  PkReportHeatmapRow;
        'pk-report-heatmap-cell': PkReportHeatmapCell;
    }
}
